import { BookingOptions, ExperienceOffer, TimeGroups, TransferType } from '~/services/ExperiencesService';

import { pluralizeToString } from './stringUtils';

export function formatOccupants(adults?: number, children?: number): string {
  return [pluralizeToString('adult', adults), (children ?? 0) > 0 ? pluralizeToString('child', children ?? 0) : '']
    .filter(Boolean)
    .join(', ');
}

export function formatSeats(childSeats?: number, boosterSeats?: number): string {
  return [
    childSeats ? pluralizeToString('child seat', childSeats) : undefined,
    boosterSeats ? pluralizeToString('booster seat', boosterSeats) : undefined,
  ]
    .filter(Boolean)
    .join(', ');
}

export function formatTransferTravellers(travellers: App.ExperienceDealOptions) {
  const { adults, children, child_seats, booster_seats } = travellers;

  let travellerText = formatOccupants(adults, children);
  if (children && (child_seats || booster_seats)) {
    travellerText += ` (${formatSeats(child_seats, booster_seats)})`;
  }
  return travellerText;
}

export function isAirportTransfer(experienceItem: App.ExperienceItem) {
  const airportTransferTypes = ['AIRPORT-TO-HOTEL', 'HOTEL-TO-AIRPORT'];
  return airportTransferTypes.includes(experienceItem.ticket?.type || '');
}

export function isVoucherBasedExperienceOffer(offer: ExperienceOffer) {
  return offer.bookingType.startsWith('NO-CALENDAR-');
}

export const getPackageName = (packageData: App.Package) => {
  if ('number_of_nights' in packageData) {
    return `${packageData.number_of_nights} nights ${packageData.name}`;
  } else {
    return `${packageData.name}`;
  }
};

export interface ExperienceCostCalculation {
  costPriceAud: number;
  details: Array<{
    offerId: string;
    name: string;
    ticketCount: number;
    cost: number;
    transferTickets: number;
    nonTransferTickets: number;
    luxPlusFiltered: number;
  }>;
}
export function calculateExperienceCostsForPackage(
  packageSalesforceId: string,
  experienceOffers: Array<ExperienceOffer>,
  experiencesTimesByOfferId: Record<string, Array<TimeGroups>>,
): ExperienceCostCalculation {
  try {
    if (experienceOffers.length === 0) {
      return { costPriceAud: 0, details: [] };
    }

    let totalCost = 0;
    const experienceDetails: Array<{
      offerId: string;
      name: string;
      ticketCount: number;
      cost: number;
      transferTickets: number;
      nonTransferTickets: number;
      luxPlusFiltered: number;
    }> = [];

    for (const experience of experienceOffers) {
      try {
        const timeSlots = experiencesTimesByOfferId[experience.id];
        if (!timeSlots) {
          console.error('Failed to get time slots for experience', {
            experienceOfferId: experience.id,
            packageSalesforceId,
          });
          continue;
        }

        const allTickets: Array<{
          isLuxplusExclusive: boolean;
          productId: string;
          costPrice?: { amount: number; currencyCode: string };
          salesPrices?: Array<{ currencyCode: string }>;
          transferTypes?: BookingOptions['transferTypes'];
          purchaseLimit?: TimeGroups['slots'][number]['purchaseLimit'];
          isComplimentary?: boolean;
        }> = [];

        for (const timeGroup of timeSlots) {
          for (const slot of timeGroup.slots) {
            for (const ticket of slot.tickets) {
              allTickets.push({
                isLuxplusExclusive: ticket.isLuxplusExclusive,
                productId: ticket.productId,
                costPrice: ticket.costPrice,
                salesPrices: ticket.salesPrices,
                transferTypes: ticket.bookingOptions?.transferTypes,
                purchaseLimit: slot.purchaseLimit,
                isComplimentary: ticket.isComplimentary,
              });
            }
          }
        }

        if (allTickets.length === 0) {
          continue;
        }

        // Filter out LuxPlus+ tickets for consistent pricing
        const publicTickets = allTickets.filter((ticket) => !ticket.isLuxplusExclusive);
        const luxPlusFiltered = allTickets.length - publicTickets.length;

        const complimentaryTickets = allTickets.filter((ticket) => ticket.isComplimentary);

        if (complimentaryTickets.length === 0) {
          continue;
        }

        let experienceCost = 0;
        let transferTickets = 0;
        let nonTransferTickets = 0;

        const isTransfer = experience.features?.transfer;
        const tickets = isTransfer
          ? complimentaryTickets.filter(
              (ticket) =>
                ticket.transferTypes?.includes(TransferType.AIRPORT_TO_HOTEL) ||
                ticket.transferTypes?.includes(TransferType.HOTEL_TO_AIRPORT),
            )
          : complimentaryTickets;

        for (const ticket of tickets) {
          const ticketMinPurchase = ticket.purchaseLimit?.min || 0;
          const transferTypeCount = isTransfer ? new Set(ticket.transferTypes).size : 1;
          const ticketCost = (ticket.costPrice?.amount || 0) * ticketMinPurchase * transferTypeCount;
          experienceCost += ticketCost;

          if (isTransfer) {
            transferTickets += ticketMinPurchase;
          } else {
            nonTransferTickets += ticketMinPurchase;
          }
        }

        totalCost += experienceCost;

        // Track details for logging
        experienceDetails.push({
          offerId: experience.id,
          name: experience.title,
          ticketCount: publicTickets.length,
          cost: experienceCost,
          transferTickets,
          nonTransferTickets,
          luxPlusFiltered,
        });
      } catch (error) {
        console.error('Failed to fetch tickets for experience', {
          experienceOfferId: experience.id,
          packageSalesforceId,
          error: error.message,
        });
        continue;
      }
    }

    return {
      costPriceAud: totalCost,
      details: experienceDetails,
    };
  } catch (error) {
    console.error('Failed to calculate experience costs for package', {
      packageSalesforceId,
      error: error.message,
    });
    return { costPriceAud: 0, details: [] };
  }
}
